/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, Suspense } from "react";
import { useSession } from "../context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../components/providers/ToastProvider";
import confetti from "canvas-confetti";
import api from "../services/api";

function AIHubPageContent() {
  const { data: session, status } = useSession();
  const navigate = useNavigate();
  const params = useParams();

  useEffect(() => {
    if (status === "authenticated" && (session?.user as any)?.highest_tier_level === 0) {
      navigate("/dashboard");
    }
  }, [status, session, navigate]);

  const [profiles, setProfiles] = useState<any[]>([]);
  const [profile, setProfile] = useState<any>(null);
  const [posterPrompts, setPosterPrompts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeItem, setActiveItem] = useState<any>(null);
  const [activePosterPrompt, setActivePosterPrompt] = useState<any>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { showToast } = useToast();

  const [usage, setUsage] = useState<any>(null);

  const [activePromptResult, setActivePromptResult] = useState<any | null>(null);
  const [showPromptResultDialog, setShowPromptResultDialog] = useState(false);
  const [showCopiedDialog, setShowCopiedDialog] = useState(false);
  const [startingReinvent, setStartingReinvent] = useState(false);
  const [startingOneStep, setStartingOneStep] = useState(false);

  const userId = session?.user?.id;
  const promptId = params?.id;

  const isOriginalProfile = (item: any) => {
    if (!profiles || profiles.length === 0) return true;
    const sorted = [...profiles].sort((a: any, b: any) => new Date(a.created_at || a.createdAt).getTime() - new Date(b.created_at || b.createdAt).getTime());
    return sorted[0]?.id === item.id;
  };

  const fetchData = async () => {
    if (!userId) return;
    try {
      const [profileRes, promptsRes] = await Promise.all([
        api.get(`/auth/status?userId=${userId}`),
        api.get(`/auth/poster-prompts?userId=${userId}`)
      ]);

      const profileData = profileRes.data;
      setProfile(profileData.profile);
      setProfiles(profileData.profiles || []);

      if (promptId) {
        const matchedProfile = (profileData.profiles || []).find((p: any) => p.id === promptId);
        if (matchedProfile) {
          setActiveItem(matchedProfile);
        } else if (promptId === "-1" && profileData.profile) {
          setActiveItem(profileData.profile);
        }
      }

      const promptsData = promptsRes.data;
      setPosterPrompts(promptsData.prompts || []);
      if (promptId) {
        const found = (promptsData.prompts || []).find((p: any) => p.id === promptId);
        if (found) setActivePosterPrompt(found);
      }
    } catch (err) {
      console.error("Failed to fetch AI Hub data:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsage = async () => {
    if (!userId) return;
    try {
      const res = await api.get(`/auth/membership-usage?userId=${userId}`);
      setUsage(res.data);
    } catch (err) {
      console.error("Failed to fetch membership usage:", err);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchData();
      fetchUsage();
    }
  }, [userId, promptId]);

  useEffect(() => {
    const checkGeneratedPrompt = () => {
      const storedPrompt = localStorage.getItem("showPromptResultDialog");
      if (storedPrompt) {
        try {
          const parsed = JSON.parse(storedPrompt);
          setActivePromptResult(parsed);
          setShowPromptResultDialog(true);

          const duration = 2000;
          const animationEnd = Date.now() + duration;
          const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 110 };
          const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

          const interval: any = setInterval(function () {
            const timeLeft = animationEnd - Date.now();
            if (timeLeft <= 0) return clearInterval(interval);
            const particleCount = 40 * (timeLeft / duration);
            confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
            confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
          }, 200);

          localStorage.removeItem("showPromptResultDialog");
          fetchData();
        } catch (e) {
          console.error("Failed to parse stored prompt result:", e);
        }
      }
    };

    if (userId && profile) {
      checkGeneratedPrompt();
    }
  }, [userId, profile]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showToast(
      "AI Prompt Copied!",
      "The prompt is now in your clipboard. Use this in Midjourney or Canva AI to generate your ad poster.",
      "success"
    );
    setShowCopiedDialog(true);
  };

  const handleCreateNewPoster = () => {
    if (!profile) {
      navigate("/activation");
    } else {
      setShowCreateModal(true);
    }
  };

  const handleStartReinvent = async () => {
    if (!userId) return;
    setStartingReinvent(true);
    try {
      const res = await api.post(`/auth/activate/start-reinvent`, { userId });
      setShowCreateModal(false);
      navigate(`/activation?profileId=${res.data.profileId}&reinvent=true`);
    } catch (err: any) {
      console.error("Failed to start re-invention:", err);
      const errorData = err.response?.data || {};
      if (err.response?.status === 403 && errorData.limitReached) {
        showToast(
          "Quota Reached",
          `You have reached your limit of ${errorData.limit} creations. Please contact your coach to upgrade!`,
          "error"
        );
      } else {
        showToast("Failed to Start", errorData.error || "Failed to start re-invention.", "error");
      }
    } finally {
      setStartingReinvent(false);
    }
  };

  const handleStartOneStep = async () => {
    if (!userId) return;
    setStartingOneStep(true);
    try {
      const res = await api.post(`/auth/activate/start-reinvent`, { userId });
      setShowCreateModal(false);
      navigate(`/one-step-poster?profileId=${res.data.profileId}`);
    } catch (err: any) {
      console.error("Failed to start One Step Poster:", err);
      const errorData = err.response?.data || {};
      if (err.response?.status === 403 && errorData.limitReached) {
        showToast(
          "Quota Reached",
          `You have reached your limit of ${errorData.limit} creations. Please contact your coach to upgrade!`,
          "error"
        );
      } else {
        showToast("Failed to Start", errorData.error || "Failed to start One Step Poster.", "error");
      }
    } finally {
      setStartingOneStep(false);
    }
  };

  // Dynamic Poster Prompt Styling Helper
  const getPosterSourceStyles = (source: string) => {
    if (source === "custom_viral" || source === "custom_viral_poster") {
      return {
        label: "Custom Viral Poster",
        icon: "rocket_launch",
        hoverBorder: "hover:border-amber-400/40",
        glow: "bg-amber-500/5 group-hover:bg-amber-500/10",
        iconBg: "bg-amber-500/10 text-amber-500",
        badge: "bg-amber-500/10 text-amber-600 border border-amber-500/10",
        textColor: "text-amber-500",
        hoverTextColor: "group-hover:text-amber-500",
        textLightColor: "text-amber-400"
      };
    }
    if (source === "custom_brand" || source === "custom_brand_poster") {
      return {
        label: "Custom Brand Poster",
        icon: "diamond",
        hoverBorder: "hover:border-violet-400/40",
        glow: "bg-violet-500/5 group-hover:bg-violet-500/10",
        iconBg: "bg-violet-500/10 text-violet-500",
        badge: "bg-violet-500/10 text-violet-600 border border-violet-500/10",
        textColor: "text-violet-500",
        hoverTextColor: "group-hover:text-violet-500",
        textLightColor: "text-violet-400"
      };
    }
    if (source === "custom_offer" || source === "custom_offer_poster") {
      return {
        label: "Custom Offer Poster",
        icon: "local_fire_department",
        hoverBorder: "hover:border-rose-400/40",
        glow: "bg-rose-500/5 group-hover:bg-rose-500/10",
        iconBg: "bg-rose-500/10 text-rose-500",
        badge: "bg-rose-500/10 text-rose-600 border border-rose-500/10",
        textColor: "text-rose-500",
        hoverTextColor: "group-hover:text-rose-500",
        textLightColor: "text-rose-400"
      };
    }
    return {
      label: "Custom Poster",
      icon: "auto_awesome",
      hoverBorder: "hover:border-sky-400/40",
      glow: "bg-sky-500/5 group-hover:bg-sky-500/10",
      iconBg: "bg-sky-500/10 text-sky-500",
      badge: "bg-sky-500/10 text-sky-600 border border-sky-500/10",
      textColor: "text-sky-500",
      hoverTextColor: "group-hover:text-sky-500",
      textLightColor: "text-sky-400"
    };
  };

  const activePosterPromptStyles = activePosterPrompt ? getPosterSourceStyles(activePosterPrompt.source) : null;

  // Unified chronological feed
  const getProfileProgress = (item: any) => {
    if (!item) return 0;
    if (item.selected_poster_prompt) return 100;
    if (item.selected_caption) return 80;
    if (item.selected_positioning) return 60;
    if (item.selected_audience) return 40;
    if (item.target_audiences) return 20;
    return 0;
  };

  const inProgressCount = profiles.filter((p: any) => getProfileProgress(p) < 100).length;

  const getCompletedCreationsCount = () => {
    if (profiles.length === 0) return 0;
    const sorted = [...profiles].sort((a: any, b: any) => new Date(a.created_at || a.createdAt).getTime() - new Date(b.created_at || b.createdAt).getTime());
    const firstProfileId = sorted[0]?.id;
    const completedStrategies = profiles.filter((p: any) => {
      if (p.id === firstProfileId) return false;
      return getProfileProgress(p) === 100;
    }).length;
    const completedPosters = posterPrompts.filter((pp: any) => pp.source !== "activation" && pp.source !== "reinvent" && pp.source !== "one_step_poster").length;
    return completedStrategies + completedPosters;
  };

  const allItems = [
    ...profiles.map((p: any) => ({ ...p, _itemType: "brand_activation" })),
    ...posterPrompts.filter((pp: any) => pp.source !== "activation" && pp.source !== "reinvent" && pp.source !== "one_step_poster").map((pp: any) => ({ ...pp, _itemType: "poster_prompt" }))
  ].sort((a, b) => new Date(b.created_at || b.createdAt).getTime() - new Date(a.created_at || a.createdAt).getTime());

  if (status === "loading" || loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
          <p className="text-xs font-black uppercase tracking-widest text-slate-500">Retrieving Intelligence...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-10">
      {activeItem === null && activePosterPrompt === null ? (
        /* ==================== LIST VIEW ==================== */
        <div className="space-y-10 animate-in fade-in duration-500">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 px-2">
            <div className="space-y-1">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center text-primary">
                  <span className="material-symbols-outlined font-black">auto_awesome</span>
                </div>
                <div>
                  <h2 className="text-3xl font-black text-on-surface tracking-tighter leading-tight">AI Hub</h2>
                  <p className="text-sm font-medium text-slate-500">All your brand activations and poster prompts in one place.</p>
                </div>
              </div>
            </div>
            <div className="flex gap-3 w-full md:w-auto">
              <button
                onClick={handleCreateNewPoster}
                className="flex-1 md:flex-none px-6 py-3 bg-primary text-white rounded-xl font-black text-[10px] uppercase tracking-widest shadow-xl shadow-primary/10 hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-2"
              >
                <span className="material-symbols-outlined text-sm">add</span>
                Create New Poster
              </button>
            </div>
          </div>

          {/* Quota Stats Cards */}
          {usage && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Made Card */}
              <div className="bg-surface-container-low/40 backdrop-blur-sm border border-outline-variant/10 rounded-[2.2rem] p-6 shadow-md hover:shadow-xl hover:shadow-primary/5 hover:border-primary/20 hover:-translate-y-1 transition-all duration-300 flex items-center justify-between group overflow-hidden relative">
                <div className="absolute top-0 right-0 w-24 h-24 bg-primary/5 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2 group-hover:scale-110 transition-transform duration-500" />
                <div className="relative z-10 space-y-1">
                  <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest block">Made</span>
                  <div className="text-3xl font-black text-on-surface tracking-tight mt-1">
                    {getCompletedCreationsCount()}
                  </div>
                  <p className="text-[10px] font-semibold text-slate-400">Creations completed</p>
                </div>
                <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center text-primary group-hover:scale-110 transition-all duration-300 shadow-sm border border-primary/5 relative z-10">
                  <span className="material-symbols-outlined font-black text-xl">auto_awesome</span>
                </div>
              </div>

              {/* In Progress Card */}
              <div className="bg-surface-container-low/40 backdrop-blur-sm border border-outline-variant/10 rounded-[2.2rem] p-6 shadow-md hover:shadow-xl hover:shadow-amber-500/5 hover:border-amber-500/20 hover:-translate-y-1 transition-all duration-300 flex items-center justify-between group overflow-hidden relative">
                <div className="absolute top-0 right-0 w-24 h-24 bg-amber-500/5 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2 group-hover:scale-110 transition-transform duration-500" />
                <div className="relative z-10 space-y-1">
                  <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest block">In Progress</span>
                  <div className="text-3xl font-black text-on-surface tracking-tight mt-1">
                    {inProgressCount}
                  </div>
                  <p className="text-[10px] font-semibold text-slate-400">Currently processing</p>
                </div>
                <div className="w-12 h-12 bg-amber-500/10 rounded-2xl flex items-center justify-center text-amber-500 group-hover:scale-110 transition-all duration-300 shadow-sm border border-amber-500/5 relative z-10">
                  <span className="material-symbols-outlined font-black text-xl">hourglass_empty</span>
                </div>
              </div>

              {/* Remaining Card */}
              <div className="bg-surface-container-low/40 backdrop-blur-sm border border-outline-variant/10 rounded-[2.2rem] p-6 shadow-md hover:shadow-xl hover:shadow-emerald-500/5 hover:border-emerald-500/20 hover:-translate-y-1 transition-all duration-300 flex items-center justify-between group overflow-hidden relative">
                <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/5 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2 group-hover:scale-110 transition-transform duration-500" />
                <div className="relative z-10 space-y-1">
                  <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest block">Remaining</span>
                  <div className="text-3xl font-black text-on-surface tracking-tight mt-1">
                    {usage.isExempt ? "∞" : Math.max(0, usage.totalLimit - usage.currentUsage)}
                  </div>
                  <p className="text-[10px] font-semibold text-slate-400">
                    {usage.isExempt
                      ? "Unlimited developer access"
                      : usage.isMonthly
                      ? "Resets monthly"
                      : "One-time quota"}
                  </p>
                </div>
                <div className="w-12 h-12 bg-emerald-500/10 rounded-2xl flex items-center justify-center text-emerald-500 group-hover:scale-110 transition-all duration-300 shadow-sm border border-emerald-500/5 relative z-10">
                  <span className="material-symbols-outlined font-black text-xl">
                    {usage.isExempt ? "all_inclusive" : "stars"}
                  </span>
                </div>
              </div>
            </div>
          )}

          {allItems.length === 0 ? (
            /* Empty State */
            <div className="bg-surface-container-low border border-outline-variant/10 rounded-[3rem] p-12 md:p-20 text-center space-y-8 shadow-sm max-w-4xl mx-auto">
              <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6 text-primary relative">
                <div className="absolute inset-0 bg-primary/10 rounded-full animate-ping opacity-75" />
                <span className="material-symbols-outlined text-4xl font-black relative z-10">auto_awesome</span>
              </div>
              <h3 className="text-3xl font-black tracking-tighter text-on-surface">No AI Marketing Strategy Yet</h3>
              <p className="text-on-surface-variant max-w-md mx-auto text-sm font-medium leading-relaxed">
                Start your activation to set up your business profile, find your best customers, and generate AI poster prompts for your ads.
              </p>
              <button
                onClick={handleCreateNewPoster}
                className="px-8 py-4 bg-primary text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-105 active:scale-95 transition-all inline-flex items-center gap-2 animate-bounce"
              >
                <span className="material-symbols-outlined text-sm">rocket_launch</span>
                Start AI Activation
              </button>
            </div>
          ) : (
            /* Unified Feed */
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {allItems.map((item: any) => {
                if (item._itemType === "brand_activation") {
                  const progress = getProfileProgress(item);
                  const isIncomplete = progress < 100;
                  return (
                    <div
                      key={item.id}
                      onClick={() => {
                        if (isIncomplete) {
                          const isOrig = isOriginalProfile(item);
                          navigate(`/activation?profileId=${item.id}${!isOrig ? "&reinvent=true" : ""}`);
                        } else {
                          setActiveItem(item);
                        }
                      }}
                      className={`bg-surface-container-low border border-outline-variant/10 p-6 rounded-3xl shadow-sm flex flex-col justify-between relative overflow-hidden group cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all duration-300 min-h-[240px] ${
                        isIncomplete ? "hover:border-amber-500/40" : "hover:border-emerald-500/40"
                      }`}
                    >
                      <div className={`absolute top-0 right-0 w-28 h-28 rounded-full -mr-14 -mt-14 blur-2xl transition-colors duration-500 ${
                        isIncomplete
                          ? "bg-amber-500/5 group-hover:bg-amber-500/10"
                          : "bg-emerald-500/5 group-hover:bg-emerald-500/10"
                      }`} />
                      <div className="space-y-4 relative z-10">
                        <div className="flex justify-between items-center">
                          <div className={`w-9 h-9 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 ${
                            isIncomplete
                              ? "bg-amber-500/10 text-amber-600"
                              : "bg-emerald-500/10 text-emerald-600"
                          }`}>
                            <span className="material-symbols-outlined text-base">psychology</span>
                          </div>
                          {isIncomplete ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-500/10 text-amber-700 rounded-lg text-[8px] font-black uppercase tracking-widest border border-amber-500/10">
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                              Pending ({progress}%)
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-500/10 text-emerald-700 rounded-lg text-[8px] font-black uppercase tracking-widest border border-emerald-500/10">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                              Brand Activation
                            </span>
                          )}
                        </div>
                        <div className="space-y-1">
                          <h3 className={`text-base font-black tracking-tight text-on-surface transition-colors line-clamp-1 leading-snug ${
                            isIncomplete ? "group-hover:text-amber-600" : "group-hover:text-emerald-600"
                          }`}>
                            {item.product_service_name || item.business_name || "Brand Strategy"}
                          </h3>
                          <p className="text-[10px] font-bold text-slate-500 flex items-center gap-1.5">
                            <span>{item.category}</span>
                            {item.category && item.location && <span>•</span>}
                            <span>{item.location}</span>
                          </p>
                        </div>
                        <p className="text-[11px] text-on-surface-variant leading-relaxed line-clamp-2">
                          AI-generated target segments, positioning angles, ad captions, and poster prompts for your brand.
                        </p>
                        {isIncomplete && (
                          <div className="space-y-1.5 pt-2">
                            <div className="flex justify-between items-center text-[9px] font-black uppercase tracking-widest text-slate-400">
                              <span>Progress</span>
                              <span>{progress}%</span>
                            </div>
                            <div className="w-full bg-slate-100 dark:bg-slate-800/50 rounded-full h-1.5 overflow-hidden">
                              <div className="bg-amber-500 h-1.5 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="pt-4 border-t border-outline-variant/10 flex justify-between items-center relative z-10 mt-4">
                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                          {new Date(item.created_at || item.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                        {isIncomplete ? (
                          <span className="text-amber-600 font-black text-[10px] uppercase tracking-widest flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                            Resume Creation
                            <span className="material-symbols-outlined text-xs">arrow_forward</span>
                          </span>
                        ) : (
                          <span className="text-emerald-600 font-black text-[10px] uppercase tracking-widest flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                            Open Strategy
                            <span className="material-symbols-outlined text-xs">arrow_forward</span>
                          </span>
                        )}
                      </div>
                    </div>
                  );
                } else {
                  const styles = getPosterSourceStyles(item.source);
                  return (
                    <div
                      key={item.id}
                      onClick={() => setActivePosterPrompt(item)}
                      className={`bg-surface-container-low border border-outline-variant/10 p-6 rounded-3xl shadow-sm flex flex-col justify-between relative overflow-hidden group cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all duration-300 min-h-[220px] ${styles.hoverBorder}`}
                    >
                      <div className={`absolute top-0 right-0 w-28 h-28 rounded-full -mr-14 -mt-14 blur-2xl transition-colors duration-500 ${styles.glow}`} />
                      <div className="space-y-4 relative z-10">
                        <div className="flex justify-between items-center">
                          <div className={`w-9 h-9 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 ${styles.iconBg}`}>
                            <span className="material-symbols-outlined text-base">{styles.icon}</span>
                          </div>
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[8px] font-black uppercase tracking-widest ${styles.badge}`}>
                            <span className="material-symbols-outlined text-[10px]">image</span>
                            Poster Prompt
                          </span>
                        </div>
                        <div className="space-y-1">
                          <h3 className={`text-base font-black tracking-tight text-on-surface transition-colors line-clamp-2 leading-snug ${styles.hoverTextColor}`}>
                            {item.description || "Custom Poster Prompt"}
                          </h3>
                          <p className="text-[10px] font-bold text-slate-500">
                            {styles.label}
                          </p>
                        </div>
                        <p className="text-[11px] text-on-surface-variant leading-relaxed line-clamp-2 font-mono">
                          {item.pro_prompt?.substring(0, 100)}...
                        </p>
                      </div>
                      <div className="pt-4 border-t border-outline-variant/10 flex justify-between items-center relative z-10 mt-4">
                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                          {new Date(item.created_at || item.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                        <span className={`font-black text-[10px] uppercase tracking-widest flex items-center gap-1 group-hover:translate-x-1 transition-transform ${styles.textColor}`}>
                          View Prompt
                          <span className="material-symbols-outlined text-xs">arrow_forward</span>
                        </span>
                      </div>
                    </div>
                  );
                }
              })}
            </div>
          )}
        </div>
      ) : activePosterPrompt !== null ? (
        /* ==================== POSTER PROMPT DETAIL VIEW ==================== */
        <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 px-2">
            <button
              onClick={() => { setActivePosterPrompt(null); navigate("/ai-hub"); }}
              className="flex items-center gap-2 px-5 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 hover:text-primary hover:bg-primary/5 rounded-xl transition-all border border-outline-variant/10 shadow-sm bg-surface-container-low"
            >
              <span className="material-symbols-outlined text-base">arrow_back</span>
              Back to AI Hub
            </button>
            <div className="flex gap-3 w-full md:w-auto">
              <button
                onClick={handleCreateNewPoster}
                className="flex-1 md:flex-none px-6 py-3 bg-primary text-white rounded-xl font-black text-[10px] uppercase tracking-widest shadow-xl shadow-primary/10 hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-2"
              >
                <span className="material-symbols-outlined text-sm">add</span>
                Create New Poster
              </button>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-surface-container-low border border-outline-variant/10 p-8 rounded-[3rem] shadow-sm relative overflow-hidden">
              <div className={`absolute top-0 right-0 w-48 h-48 rounded-full -mr-24 -mt-24 blur-3xl ${activePosterPromptStyles?.glow}`} />
              <div className="space-y-3 relative z-10">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${activePosterPromptStyles?.iconBg}`}>
                    <span className="material-symbols-outlined font-black">{activePosterPromptStyles?.icon}</span>
                  </div>
                  <div>
                    <h2 className="text-2xl md:text-3xl font-black text-on-surface tracking-tighter leading-tight">
                      {activePosterPrompt.description || "Custom Poster Prompt"}
                    </h2>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest ${activePosterPromptStyles?.badge}`}>
                        <span className="material-symbols-outlined text-[10px]">image</span>
                        {activePosterPromptStyles?.label}
                      </span>
                      <span className="text-slate-300">•</span>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                        Generated {new Date(activePosterPrompt.created_at || activePosterPrompt.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {activePosterPrompt.metadata?.variations && Array.isArray(activePosterPrompt.metadata.variations) ? (
              <div className="space-y-6">
                <div className="flex items-center gap-3 px-1">
                  <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center text-primary">
                    <span className="material-symbols-outlined text-xl">auto_awesome</span>
                  </div>
                  <span className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-500">AI Poster Variations</span>
                </div>
                <div className="grid grid-cols-1 gap-6">
                  {activePosterPrompt.metadata.variations.map((variation: any, idx: number) => {
                    const varPrompt = variation.pro_prompt || variation.visual_ai_prompt || variation.ai_prompt;
                    const varConcept = variation.concept_name || variation.description || `Variation ${idx + 1}`;
                    return (
                      <div key={idx} className="bg-slate-900 border-2 border-slate-800 p-8 rounded-[3rem] shadow-2xl flex flex-col gap-8 relative overflow-hidden group/card w-full">
                        <div className="absolute -top-24 -right-24 w-80 h-80 bg-sky-500/10 rounded-full blur-[100px] pointer-events-none opacity-50" />
                        <div className="flex flex-col gap-6 relative z-10">
                          <div className="space-y-2">
                            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-primary">Visual Concept</span>
                            <h4 className="text-lg font-bold text-slate-200 tracking-tight leading-relaxed italic">
                              {varConcept}
                            </h4>
                          </div>
                          <div className="space-y-3">
                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 block">Visual Generation Code</span>
                            <div className="relative overflow-hidden rounded-[2rem] border border-white/5 bg-black/40 shadow-xl">
                              <div className="p-6 min-h-[120px] flex items-center">
                                <p className="text-xs md:text-sm font-medium text-slate-300 leading-relaxed font-mono select-all">
                                  {varPrompt}
                                </p>
                              </div>
                              <div className="bg-white/5 border-t border-white/5 p-3 flex justify-end">
                                <button
                                  onClick={() => copyToClipboard(varPrompt)}
                                  className="px-6 py-3 bg-primary text-white rounded-lg font-black text-[10px] uppercase tracking-widest hover:scale-105 active:scale-95 transition-all flex items-center gap-2 shadow-md cursor-pointer"
                                >
                                  <span className="material-symbols-outlined text-sm">content_copy</span>
                                  Copy Prompt
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="bg-slate-900 border-2 border-slate-800 p-8 rounded-[3rem] shadow-2xl relative overflow-hidden">
                <div className="absolute -top-24 -right-24 w-80 h-80 bg-sky-500/10 rounded-full blur-[100px] pointer-events-none opacity-50" />
                <div className="flex flex-col gap-6 relative z-10">
                  <span className={`text-[10px] font-black uppercase tracking-[0.3em] ${activePosterPromptStyles?.textLightColor}`}>Visual Generation Code</span>
                  <div className="relative overflow-hidden rounded-[2rem] border border-white/5 bg-black/40 shadow-xl">
                    <div className="p-6 min-h-[120px] flex items-center">
                      <p className="text-xs md:text-sm font-medium text-slate-300 leading-relaxed font-mono select-all">
                        {activePosterPrompt.pro_prompt}
                      </p>
                    </div>
                    <div className="bg-white/5 border-t border-white/5 p-3 flex justify-end">
                      <button
                        onClick={() => copyToClipboard(activePosterPrompt.pro_prompt)}
                        className="px-6 py-3 bg-primary text-white rounded-lg font-black text-[10px] uppercase tracking-widest hover:scale-105 active:scale-95 transition-all flex items-center gap-2 shadow-md cursor-pointer"
                      >
                        <span className="material-symbols-outlined text-sm">content_copy</span>
                        Copy Prompt
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* ==================== ACTIVATION DETAIL VIEW ==================== */
        <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 px-2">
            <button
              onClick={() => { setActiveItem(null); navigate("/ai-hub"); }}
              className="flex items-center gap-2 px-5 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 hover:text-primary hover:bg-primary/5 rounded-xl transition-all border border-outline-variant/10 shadow-sm bg-surface-container-low"
            >
              <span className="material-symbols-outlined text-base">arrow_back</span>
              Back to AI Hub
            </button>
            <div className="flex gap-3 w-full md:w-auto">
              <button
                onClick={handleCreateNewPoster}
                className="flex-1 md:flex-none px-6 py-3 bg-primary text-white rounded-xl font-black text-[10px] uppercase tracking-widest shadow-xl shadow-primary/10 hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-2"
              >
                <span className="material-symbols-outlined text-sm">add</span>
                Create New Poster
              </button>
            </div>
          </div>

          {/* Strategy Header */}
          <div className="bg-surface-container-low border border-outline-variant/10 p-8 rounded-[3rem] shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-48 h-48 bg-primary/5 rounded-full -mr-24 -mt-24 blur-3xl" />
            <div className="space-y-3 relative z-10">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center text-primary">
                  <span className="material-symbols-outlined font-black">business_center</span>
                </div>
                <div>
                  <h2 className="text-3xl font-black text-on-surface tracking-tighter leading-tight">
                    {activeItem.product_service_name}
                  </h2>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    <p className="text-xs font-semibold text-slate-500">
                      {activeItem.category} • {activeItem.location}
                    </p>
                    <span className="text-slate-300">•</span>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-500/10 text-emerald-600 rounded-lg text-[9px] font-black uppercase tracking-widest border border-emerald-500/10">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                      Brand Activation
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2.5 pt-2">
                {activeItem.selected_strategy && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 rounded-lg text-[9px] font-black uppercase tracking-widest border border-emerald-500/10">
                    {activeItem.selected_strategy === 'viral_poster' ? 'Viral Strategy' : activeItem.selected_strategy === 'brand_poster' ? 'Brand Strategy' : 'Offer Strategy'}
                  </span>
                )}
                {activeItem.selected_language && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-500/10 text-blue-700 dark:text-blue-400 rounded-lg text-[9px] font-black uppercase tracking-widest border border-blue-500/10">
                    {activeItem.selected_language === 'simple_english' ? 'Simple English' : activeItem.selected_language === 'manglish' ? 'Manglish' : 'Malayalam'}
                  </span>
                )}
              </div>
            </div>
            <div className="text-left md:text-right shrink-0 relative z-10">
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Activation Date</span>
              <span className="text-lg font-black text-on-surface">
                {new Date(activeItem.created_at || activeItem.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
              </span>
            </div>
          </div>

          {/* Strategic Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2 bg-surface-container-low border border-outline-variant/10 p-8 rounded-[2.5rem] shadow-sm flex flex-col justify-center gap-3 relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-24 h-24 bg-primary/5 rounded-full -mr-12 -mt-12 blur-2xl group-hover:bg-primary/10 transition-colors" />
              <div className="flex items-center gap-3 relative z-10">
                <span className="material-symbols-outlined text-primary text-sm">auto_fix_high</span>
                <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">What Makes It Special</h3>
              </div>
              <p className="text-sm font-bold text-on-surface-variant leading-relaxed relative z-10">{activeItem.key_features}</p>
            </div>
            <div className="bg-surface-container-low border border-outline-variant/10 p-8 rounded-[2.5rem] shadow-sm flex flex-col justify-center gap-5">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-emerald-500 text-sm">chat</span>
                  <h4 className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-500">WhatsApp Hooks</h4>
                </div>
                <p className="text-sm font-bold text-on-surface-variant leading-relaxed">
                  {activeItem.whatsapp_hook || "Custom automated conversational ad hooks."}
                </p>
              </div>
            </div>
          </div>

          {/* Positioning */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 px-1">
              <div className="w-8 h-8 bg-sky-500/10 rounded-lg flex items-center justify-center text-sky-500">
                <span className="material-symbols-outlined text-sm">rocket_launch</span>
              </div>
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Positioning Angles</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {(activeItem.positioning_options?.positioning_options || []).map((option: any, idx: number) => {
                const isSelected = (option.niche_name || option.angle) === (activeItem.selected_positioning?.niche_name || activeItem.selected_positioning?.angle);
                return (
                  <div key={idx} className={`p-6 rounded-[2.5rem] border-2 transition-all flex flex-col gap-5 relative overflow-hidden ${isSelected ? "bg-[#067a52] text-white border-transparent shadow-2xl shadow-emerald-900/40" : "border-outline-variant/10 bg-surface-container-lowest opacity-50 grayscale-[0.8] hover:opacity-100 hover:grayscale-0"}`}>
                    {isSelected && (
                      <>
                        <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-16 -mt-16 blur-2xl" />
                        <div className="flex items-center gap-2 relative z-10">
                          <span className="px-2 py-0.5 bg-white/20 backdrop-blur-md rounded text-[8px] font-black uppercase tracking-widest text-white">Selected</span>
                          <span className="flex items-center gap-1 text-[8px] font-black uppercase tracking-widest text-emerald-200">
                            <span className="material-symbols-outlined text-[10px]">verified</span>
                            Active Angle
                          </span>
                        </div>
                      </>
                    )}
                    <div className="space-y-2 relative z-10">
                      <h4 className={`text-lg font-black tracking-tight leading-tight ${isSelected ? "text-white" : "text-on-surface"}`}>{option.niche_name || option.angle}</h4>
                      <p className={`text-xs font-bold leading-relaxed ${isSelected ? "text-emerald-50/80" : "text-on-surface-variant"}`}>"{option.positioning_hook || option.positioning_statement}"</p>
                    </div>
                    <div className={`pt-4 border-t relative z-10 ${isSelected ? "border-white/10" : "border-outline-variant/10"} mt-auto`}>
                      <p className={`text-[8px] font-black uppercase tracking-widest mb-2 ${isSelected ? "text-emerald-300/60" : "text-slate-400"}`}>Why It Works</p>
                      <p className={`text-[11px] font-bold leading-tight ${isSelected ? "text-white" : "text-slate-500"}`}>{option.why_it_wins || option.the_niche_logic || (option.pain_points && option.pain_points.join(", "))}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Ad Captions */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 px-1">
              <div className="w-8 h-8 bg-pink-500/10 rounded-lg flex items-center justify-center text-pink-500">
                <span className="material-symbols-outlined text-sm">draw</span>
              </div>
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Ad Creative Copy</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {(activeItem.ad_captions?.conversion_angles || activeItem.ad_captions?.ad_captions || []).map((caption: any, idx: number) => {
                const isSelected = (caption.poster_headline || caption.hook) === (activeItem.selected_caption?.poster_headline || activeItem.selected_caption?.hook);
                return (
                  <div key={idx} className={`p-6 rounded-[2.5rem] border-2 transition-all flex flex-col gap-5 relative overflow-hidden ${isSelected ? "bg-[#067a52] text-white border-transparent shadow-2xl shadow-emerald-900/40" : "border-outline-variant/10 bg-surface-container-lowest opacity-50 grayscale-[0.8] hover:opacity-100 hover:grayscale-0"}`}>
                    {isSelected && (
                      <>
                        <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-16 -mt-16 blur-2xl" />
                        <div className="flex items-center gap-2 relative z-10">
                          <span className="px-2 py-0.5 bg-white/20 backdrop-blur-md rounded text-[8px] font-black uppercase tracking-widest text-white">Selected</span>
                          <span className="flex items-center gap-1 text-[8px] font-black uppercase tracking-widest text-emerald-200">
                            <span className="material-symbols-outlined text-[10px]">verified</span>
                            Active Ad
                          </span>
                        </div>
                      </>
                    )}
                    <div className="space-y-4 relative z-10">
                      <div className="space-y-1">
                        <p className={`text-[8px] font-black uppercase tracking-widest ${isSelected ? "text-emerald-300/60" : "text-primary"}`}>Headline / Hook</p>
                        <h4 className={`text-sm font-black tracking-tight leading-tight italic ${isSelected ? "text-white" : "text-on-surface"}`}>"{caption.poster_headline || caption.hook}"</h4>
                      </div>
                      <div className="space-y-2">
                        <div className="space-y-0.5">
                          <p className={`text-[8px] font-black uppercase tracking-widest ${isSelected ? "text-emerald-300/60" : "text-slate-400"}`}>Angle Type</p>
                          <p className={`text-[10px] font-bold ${isSelected ? "text-emerald-50/80" : "text-on-surface-variant"}`}>{caption.angle_type || "Result-Oriented"}</p>
                        </div>
                        <div className="space-y-0.5">
                          <p className={`text-[8px] font-black uppercase tracking-widest ${isSelected ? "text-emerald-300/60" : "text-emerald-500"}`}>Emotional Logic</p>
                          <p className={`text-[10px] font-bold ${isSelected ? "text-emerald-50/80" : "text-on-surface-variant"}`}>{caption.emotional_logic || caption.solution}</p>
                        </div>
                      </div>
                    </div>
                    <div className={`mt-auto p-4 rounded-2xl border relative z-10 ${isSelected ? "bg-white/10 border-white/10" : "bg-primary/5 border-primary/10"}`}>
                      <p className={`text-[8px] font-black uppercase tracking-widest mb-1 ${isSelected ? "text-emerald-300/60" : "text-primary"}`}>CTA / WhatsApp</p>
                      <p className={`text-[10px] font-black leading-tight ${isSelected ? "text-white" : "text-on-surface"}`}>{caption.tempt_to_buy_cta || caption.cta || caption.whatsapp_status_caption}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* AI Poster Prompts */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 px-1">
              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center text-primary">
                <span className="material-symbols-outlined text-xl">auto_awesome</span>
              </div>
              <span className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-500">AI Poster Prompts</span>
            </div>
            <div className="grid grid-cols-1 gap-6">
              {(() => {
                const list: any[] = [];
                if (activeItem.selected_poster_prompt && !activeItem.selected_poster_prompt.skipped) {
                  list.push({ ...activeItem.selected_poster_prompt, isSelectedPrompt: true });
                }
                const basePrompts = Array.isArray(activeItem.ai_poster_prompts?.poster_prompts)
                  ? activeItem.ai_poster_prompts?.poster_prompts
                  : (activeItem.ai_poster_prompts?.design_package || activeItem.ai_poster_prompts)
                    ? [activeItem.ai_poster_prompts?.design_package || activeItem.ai_poster_prompts]
                    : [];
                basePrompts.forEach((p: any) => {
                  if (!p) return;
                  const isDuplicate = activeItem.selected_poster_prompt &&
                    !activeItem.selected_poster_prompt.skipped &&
                    (p.pro_prompt || p.visual_ai_prompt || p.ai_prompt) ===
                    (activeItem.selected_poster_prompt.pro_prompt || activeItem.selected_poster_prompt.visual_ai_prompt || activeItem.selected_poster_prompt.ai_prompt);
                  if (!isDuplicate) list.push(p);
                });
                return list;
              })().map((prompt: any, idx: number) => (
                <div key={idx} className={`bg-slate-900 border-2 p-8 rounded-[3rem] shadow-2xl flex flex-col gap-8 relative overflow-hidden group/card w-full ${prompt.isSelectedPrompt ? "border-primary/45 shadow-lg shadow-primary/5" : "border-slate-800"}`}>
                  <div className="absolute -top-24 -right-24 w-80 h-80 bg-primary/10 rounded-full blur-[100px] pointer-events-none opacity-50" />
                  <div className="flex flex-col gap-6 relative z-10">
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[10px] font-black uppercase tracking-[0.3em] text-primary">Visual Concept</span>
                        {prompt.isSelectedPrompt && (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-500/10 text-emerald-400 rounded-full text-[9px] font-black uppercase tracking-widest border border-emerald-500/20">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                            Active Selected Design
                          </span>
                        )}
                      </div>
                      <h4 className="text-lg font-bold text-slate-200 tracking-tight leading-relaxed italic">
                        {prompt.description || prompt.concept_name || "Strategic Visual Concept"}
                      </h4>
                    </div>
                    <div className="space-y-3">
                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 block">Visual Generation Code</span>
                      <div className="relative overflow-hidden rounded-[2rem] border border-white/5 bg-black/40 shadow-xl">
                        <div className="p-6 min-h-[120px] flex items-center">
                          <p className="text-xs md:text-sm font-medium text-slate-300 leading-relaxed font-mono select-all">
                            {prompt.pro_prompt || prompt.visual_ai_prompt || prompt.ai_prompt}
                          </p>
                        </div>
                        <div className="bg-white/5 border-t border-white/5 p-3 flex justify-end">
                          <button
                            onClick={() => copyToClipboard(prompt.pro_prompt || prompt.visual_ai_prompt || prompt.ai_prompt)}
                            className="px-6 py-3 bg-primary text-white rounded-lg font-black text-[10px] uppercase tracking-widest hover:scale-105 active:scale-95 transition-all flex items-center gap-2 shadow-md cursor-pointer"
                          >
                            <span className="material-symbols-outlined text-sm">content_copy</span>
                            Copy Prompt
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Prompt History */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 px-1">
              <div className="w-10 h-10 bg-sky-500/10 rounded-xl flex items-center justify-center text-sky-500">
                <span className="material-symbols-outlined text-xl">history</span>
              </div>
              <div>
                <span className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-500 block">Prompt History</span>
                <p className="text-[10px] text-slate-400 font-bold">All saved poster prompts for this account</p>
              </div>
            </div>
            {posterPrompts.filter((pp: any) => pp.source !== "activation" && pp.source !== "reinvent" && pp.source !== "one_step_poster").length === 0 ? (
              <div className="p-8 text-center bg-slate-900 border border-slate-800 rounded-[2rem] text-slate-500 text-xs font-bold">
                No custom prompts generated yet. Use "Create New Poster" to generate one.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6">
                {posterPrompts.filter((pp: any) => pp.source !== "activation" && pp.source !== "reinvent" && pp.source !== "one_step_poster").map((pp: any) => {
                  const styles = getPosterSourceStyles(pp.source);
                  return (
                    <div key={pp.id} className="bg-slate-900 border-2 border-slate-800 p-8 rounded-[3rem] shadow-2xl flex flex-col gap-8 relative overflow-hidden group/card w-full animate-in fade-in-50 duration-500">
                      <div className="absolute -top-24 -right-24 w-80 h-80 bg-sky-500/10 rounded-full blur-[100px] pointer-events-none opacity-50" />
                      <div className="flex flex-col gap-6 relative z-10">
                        <div className="space-y-2">
                          <div className="flex flex-wrap items-center justify-between">
                            <span className={`text-[10px] font-black uppercase tracking-[0.3em] ${styles.textLightColor}`}>
                              {styles.label}
                            </span>
                            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                              {new Date(pp.created_at || pp.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                          </div>
                          <h4 className="text-lg font-bold text-slate-200 tracking-tight leading-relaxed italic">{pp.description || "Custom Poster Prompt"}</h4>
                        </div>
                        <div className="space-y-3">
                          <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 block">Visual Generation Code</span>
                          <div className="relative overflow-hidden rounded-[2rem] border border-white/5 bg-black/40 shadow-xl">
                            <div className="p-6 min-h-[120px] flex items-center">
                              <p className="text-xs md:text-sm font-medium text-slate-300 leading-relaxed font-mono select-all">{pp.pro_prompt}</p>
                            </div>
                            <div className="bg-white/5 border-t border-white/5 p-3 flex justify-end">
                              <button
                                onClick={() => copyToClipboard(pp.pro_prompt)}
                                className="px-6 py-3 bg-primary text-white rounded-lg font-black text-[10px] uppercase tracking-widest hover:scale-105 active:scale-95 transition-all flex items-center gap-2 shadow-md cursor-pointer"
                              >
                                <span className="material-symbols-outlined text-sm">content_copy</span>
                                Copy Prompt
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create New Poster Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            onClick={() => setShowCreateModal(false)}
            className="absolute inset-0 bg-slate-950/40 backdrop-blur-[6px] animate-in fade-in duration-200"
          />
          <div className="bg-surface-container-lowest border border-outline-variant/20 rounded-[2.5rem] p-8 md:p-10 max-w-4xl w-full shadow-2xl relative z-10 animate-in zoom-in-95 duration-200 flex flex-col gap-6">
            <button
              onClick={() => setShowCreateModal(false)}
              className="absolute top-6 right-6 w-9 h-9 rounded-full flex items-center justify-center bg-surface-container hover:bg-surface-container-high text-on-surface-variant hover:text-on-surface transition-all border border-outline-variant/10 shadow-sm cursor-pointer"
            >
              <span className="material-symbols-outlined text-lg">close</span>
            </button>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
                <span className="material-symbols-outlined text-2xl font-black">auto_awesome</span>
              </div>
              <div className="space-y-1">
                <h3 className="text-2xl font-black text-on-surface tracking-tight leading-tight">Create a New Poster</h3>
                <p className="text-xs font-semibold text-slate-500">Choose how you want to create your next marketing poster.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-2">
              <div className="p-6 rounded-[2rem] border border-outline-variant/10 bg-surface-container-low/50 hover:bg-surface-container-low transition-all flex flex-col justify-between gap-6 group relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-violet-500/5 rounded-full -mr-12 -mt-12 blur-2xl" />
                <div className="space-y-3 relative z-10">
                  <div className="flex flex-col gap-1.5 pt-[18px]">
                    <div className="flex items-center gap-2">
                      <span className="material-symbols-outlined text-violet-500 text-lg">bolt</span>
                      <h4 className="font-black text-sm text-on-surface">One Step Poster</h4>
                    </div>
                  </div>
                  <p className="text-[11px] font-medium leading-relaxed text-on-surface-variant">
                    Generate a viral poster directly by entering your product details. Skip campaign strategy selection and get a high-converting viral ad poster in one go.
                  </p>
                </div>
                <button
                  onClick={handleStartOneStep}
                  disabled={startingOneStep}
                  className="w-full py-4 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2 relative z-10 cursor-pointer border border-violet-500/10 disabled:opacity-50 shadow-md animate-in fade-in duration-300"
                >
                  <span className="material-symbols-outlined text-sm">bolt</span>
                  {startingOneStep ? "Starting..." : "One Step Poster"}
                </button>
              </div>

              <div className="p-6 rounded-[2rem] border-2 border-primary bg-primary/[0.03] dark:bg-primary/[0.01] transition-all flex flex-col justify-between gap-6 group shadow-md relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-primary/5 rounded-full -mr-12 -mt-12 blur-2xl" />
                <div className="space-y-3 relative z-10">
                  <div className="flex flex-col gap-1.5">
                    <span className="px-2.5 py-0.5 bg-primary/10 text-primary rounded-lg text-[9px] font-black uppercase tracking-widest border border-primary/10 max-w-fit">Recommended</span>
                    <div className="flex items-center gap-2">
                      <span className="material-symbols-outlined text-primary text-lg">psychology</span>
                      <h4 className="font-black text-sm text-on-surface">Create from Your Own Idea</h4>
                    </div>
                  </div>
                  <p className="text-[11px] font-medium leading-relaxed text-on-surface-variant">
                    Write your own ad idea or text. We will turn it into an AI poster prompt using your saved business profile.
                  </p>
                </div>
                <button
                  onClick={() => { setShowCreateModal(false); navigate("/ai-hub/new-poster"); }}
                  className="w-full py-4 bg-primary text-white rounded-xl font-black text-xs uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2 relative z-10 cursor-pointer"
                >
                  <span className="material-symbols-outlined text-sm">draw</span>
                  Create New Poster
                </button>
              </div>

              <div className="p-6 rounded-[2rem] border border-outline-variant/10 bg-surface-container-low/50 hover:bg-surface-container-low transition-all flex flex-col justify-between gap-6 group relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-slate-500/5 rounded-full -mr-12 -mt-12 blur-2xl" />
                <div className="space-y-3 relative z-10">
                  <div className="flex flex-col gap-1.5 pt-[18px]">
                    <div className="flex items-center gap-2">
                      <span className="material-symbols-outlined text-slate-500 text-lg">autorenew</span>
                      <h4 className="font-black text-sm text-on-surface">Re-invent Your Business</h4>
                    </div>
                  </div>
                  <p className="text-[11px] font-medium leading-relaxed text-on-surface-variant">
                    Start fresh with a new setup. Change your product, target customers, or business details to create a brand new marketing strategy. Your old results are kept safe.
                  </p>
                </div>
                <button
                  onClick={handleStartReinvent}
                  disabled={startingReinvent}
                  className="w-full py-4 bg-surface-container-high hover:bg-surface-container-highest text-on-surface rounded-xl font-black text-xs uppercase tracking-widest hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2 relative z-10 cursor-pointer border border-outline-variant/10 disabled:opacity-50"
                >
                  <span className="material-symbols-outlined text-sm">autorenew</span>
                  {startingReinvent ? "Starting..." : "Re-invent Business"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Prompt Result Dialog */}
      {showPromptResultDialog && activePromptResult && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-[8px]" />
          <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] md:rounded-[3.5rem] max-w-4xl w-full shadow-2xl relative z-10 animate-in zoom-in-95 duration-300 flex flex-col text-left max-h-[90vh] overflow-hidden">
            <div className="overflow-y-auto flex-1 p-8 md:p-12 pr-6 md:pr-10 space-y-8 scrollbar-thin scrollbar-thumb-slate-800 scrollbar-track-transparent">
              <div className="space-y-4">
                <span className="text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-sm">stars</span>
                  Custom Poster Prompt Ready
                </span>
                <h3 className="text-2xl md:text-3xl font-black text-slate-100 tracking-tight leading-tight">Your Custom Graphic Code</h3>
              </div>
              {activePromptResult.variations && Array.isArray(activePromptResult.variations) ? (
                <div className="space-y-6">
                  <span className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 block">AI Poster variations ({activePromptResult.variations.length})</span>
                  <div className="grid grid-cols-1 gap-6 max-h-[50vh] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-slate-800 scrollbar-track-transparent">
                    {activePromptResult.variations.map((variation: any, idx: number) => {
                      const varPrompt = variation.pro_prompt || variation.visual_ai_prompt || variation.ai_prompt;
                      const varConcept = variation.concept_name || variation.description || `Variation ${idx + 1}`;
                      return (
                        <div key={idx} className="bg-black/30 border border-white/5 p-6 rounded-2xl flex flex-col gap-4 relative overflow-hidden group/card w-full">
                          <div className="flex flex-col gap-3 relative z-10">
                            <div className="space-y-1">
                              <span className="text-[9px] font-black uppercase tracking-[0.3em] text-primary">Visual Concept</span>
                              <h4 className="text-md font-bold text-slate-200 tracking-tight leading-relaxed italic">
                                {varConcept}
                              </h4>
                            </div>
                            <div className="space-y-2">
                              <span className="text-[9px] font-black uppercase tracking-[0.3em] text-slate-500 block">Visual Generation Code</span>
                              <div className="relative overflow-hidden rounded-xl border border-white/5 bg-black/40 shadow-xl">
                                <div className="p-4 min-h-[80px] flex items-center font-mono">
                                  <p className="text-xs font-medium text-slate-300 leading-relaxed select-all">
                                    {varPrompt}
                                  </p>
                                </div>
                                <div className="bg-white/5 border-t border-white/5 p-2 flex justify-end">
                                  <button
                                    onClick={() => copyToClipboard(varPrompt)}
                                    className="px-4 py-2 bg-primary text-white rounded-lg font-black text-[9px] uppercase tracking-widest hover:scale-105 active:scale-95 transition-all flex items-center gap-1.5 shadow-md cursor-pointer"
                                  >
                                    <span className="material-symbols-outlined text-xs">content_copy</span>
                                    Copy Prompt
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="p-6 rounded-[2rem] bg-primary/10 border border-primary/20 space-y-3 relative overflow-hidden">
                    <div className="flex items-center gap-2">
                      <span className="material-symbols-outlined text-primary text-lg">psychology</span>
                      <span className="text-[10px] font-black uppercase tracking-widest text-primary">The Visual Hook</span>
                    </div>
                    <p className="text-sm font-bold text-slate-300 leading-relaxed italic">"{activePromptResult.description || "Strategic Visual Concept"}"</p>
                  </div>
                  <div className="space-y-3">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
                      <span className="material-symbols-outlined text-sm text-emerald-500">image</span>
                      Ready-to-Use Visual Prompt
                    </label>
                    <div className="relative overflow-hidden rounded-[2rem] border border-white/5 bg-black/40 shadow-xl">
                      <div className="p-8 md:p-12 min-h-[140px] flex items-center">
                        <p className="text-xs md:text-sm font-medium text-slate-300 leading-relaxed font-mono select-all">{activePromptResult.pro_prompt}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="pt-4 flex justify-end">
                <button
                  onClick={() => setShowPromptResultDialog(false)}
                  className="w-full sm:w-auto px-8 py-4 bg-primary text-white rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] hover:translate-y-[-2px] active:scale-95 transition-all flex items-center justify-center gap-2 shadow-xl shadow-primary/20 cursor-pointer"
                >
                  <span className="material-symbols-outlined text-sm">close</span>
                  Close Result
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Prompt Copied Dialog */}
      {showCopiedDialog && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div onClick={() => setShowCopiedDialog(false)} className="absolute inset-0 bg-slate-950/60 backdrop-blur-[8px]" />
          <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] md:rounded-[3.5rem] max-w-md w-full shadow-2xl shadow-emerald-500/10 relative z-10 animate-in zoom-in-95 duration-300 p-8 md:p-10 text-center flex flex-col items-center">
            <div className="relative w-20 h-20 bg-emerald-500 rounded-full flex items-center justify-center text-white shadow-xl shadow-emerald-500/20 mb-6 shrink-0 z-10">
              <span className="material-symbols-outlined text-4xl font-black">check</span>
              <div className="absolute inset-0 bg-emerald-400/30 rounded-full blur-xl animate-pulse" />
            </div>
            <div className="space-y-3 mb-6">
              <h3 className="text-2xl font-black text-slate-100 tracking-tight">AI Prompt Copied!</h3>
              <p className="text-xs text-slate-400 font-bold leading-relaxed max-w-xs mx-auto">
                The visual generation code is in your clipboard. Paste it into ChatGPT or Midjourney to create your ad poster.
              </p>
            </div>
            <div className="bg-slate-950/40 border border-white/5 p-5 rounded-2xl text-left space-y-2.5 w-full mb-6">
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">Next Steps</span>
              </div>
              <p className="text-[10px] text-slate-400 leading-relaxed font-semibold">
                1. Open ChatGPT (DALL-E 3) or Midjourney.<br />
                2. Paste your copied prompt code.<br />
                3. Generate and download your stunning ad poster.
              </p>
            </div>
            <div className="w-full space-y-3">
              <a
                href="https://chatgpt.com"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setShowCopiedDialog(false)}
                className="w-full py-5 bg-primary text-white rounded-[2rem] font-black text-xs uppercase tracking-[0.2em] shadow-2xl shadow-primary/20 hover:translate-y-[-2px] active:scale-95 transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <span className="material-symbols-outlined text-sm">open_in_new</span>
                Open ChatGPT
              </a>
              <button
                onClick={() => setShowCopiedDialog(false)}
                className="w-full py-4 text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-slate-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AIHub() {
  return (
    <Suspense fallback={
      <div className="min-h-[40vh] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
          <p className="text-xs font-black uppercase tracking-widest text-slate-500">Retrieving Intelligence...</p>
        </div>
      </div>
    }>
      <AIHubPageContent />
    </Suspense>
  );
}
